/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@mui/material';
import { OptionGroup } from '../../../types/elements/Element';
import { ELEMENTS_TYPE } from '../../../utils/elementFactory';
import { SelectedOptionsMap } from '../../../types/course/Entry';

interface Props {
	/**
	 * Array of option groups to render. Each group can display checkboxes (multiple choice)
	 * or radio buttons (single choice) based on the multiple_choice flag.
	 */
	optionsGroups: OptionGroup[];
	/**
	 * Selected options object mapping group titles or IDs to their selected values.
	 * Format: { "Test": { "options": ["Option 1", "Option2"], "selected": ["Option 1"] } }
	 * For each group, the object contains:
	 * - options: array of all available options
	 * - selected: array of selected options (single-choice will have only one item)
	 * Title-based lookup is prioritized over ID-based lookup.
	 */
	selectedOptionsObject?: SelectedOptionsMap;
	/**
	 * Element type to determine read-only vs interactive rendering.
	 */
	type: ELEMENTS_TYPE;
}

/**
 * Component that renders create-entry option groups as interactive elements with checkboxes or radio buttons.
 * Supports both single-choice (radio buttons) and multiple-choice (checkboxes) options.
 * Each group displays a title and description along with its options.
 *
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export const CreateEntryOptionsView = observer((props: Props) => {
	const { optionsGroups, selectedOptionsObject, type } = props;

	/**
	 * Checks if a specific option is selected within a group.
	 * Uses the new structure where selectedOptionsObject contains { options, selected } for each group.
	 * Supports lookup by both group ID and group title.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const isOptionSelected = (groupId: number, groupTitle: string, optionText: string): boolean => {
		if (!selectedOptionsObject) return false;

		// Try to get selected value by title first (more common format)
		let selectedData = selectedOptionsObject[groupTitle];

		// Fall back to group ID if not found by title
		if (!selectedData) {
			selectedData = selectedOptionsObject[groupId.toString()];
		}

		if (!selectedData || !selectedData.selected) return false;

		// Check if option is in the selected array
		return selectedData.selected.includes(optionText);
	};

	/**
	 * Renders the radio button control (for single-choice options).
	 */
	const renderRadioButton = (isSelected: boolean) => (
		<>
			<Box
				sx={{
					width: '16px',
					height: '16px',
					borderRadius: '50%',
					border: '1.5px solid #666',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: '#fff',
				}}>
				{isSelected && (
					<Box
						sx={{
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							background: '#222',
						}}
					/>
				)}
			</Box>
		</>
	);

	/**
	 * Renders the checkbox control (for multiple-choice options).
	 */
	const renderCheckbox = (isSelected: boolean) => (
		<img
			src={isSelected ? '/img/checkbox-checked.png' : '/img/checkbox-unchecked.png'}
			alt={isSelected ? 'Checked Checkbox' : 'Unchecked Checkbox'}
			style={{
				width: '14px',
				height: '14px',
				borderRadius: '2px',
				borderWidth: '1px',
				borderStyle: 'solid',
				borderColor: '#00000050',
			}}
		/>
	);

	return (
		<Box>
			{optionsGroups.map((optionGroup: OptionGroup) => (
				<Box key={`optionGroup-${optionGroup.id}`}>
					<Typography variant='h3' style={{ marginBottom: '8px', fontWeight: 'bold' }}>
						{optionGroup.title}
					</Typography>
					{optionGroup.description && (
						<Typography variant='body2' style={{ marginBottom: '12px', color: '#666' }}>
							{optionGroup.description}
						</Typography>
					)}
					<Box style={{ marginLeft: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{optionGroup.options?.map((optionText: string, index: number) => {
							const isSelected =
								type === ELEMENTS_TYPE.ENTRY ? isOptionSelected(optionGroup.id, optionGroup.title, optionText) : false;
							const controlKey = `option-${optionGroup.id}-${index}`;

							return (
								<Box key={controlKey} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{optionGroup.multiple_choice ? renderCheckbox(isSelected) : renderRadioButton(isSelected)}
									<Typography>{optionText}</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			))}
		</Box>
	);
});
